import React, { useEffect } from "react";
import { DockviewApi, DockviewPanelApi } from "dockview-react";
import Plot from "react-plotly.js";
import { useMemo } from "react";
import { api } from "./utils/api";

interface PricingData {
  ts_event: string;
  price: number;
  size: number;
}

interface OptionTradesData {
  ts_event: string;
  price: number;
  size: number;
  underlying_price: number;
  iv: number;
}

interface OptionIvData {
  ts_event: string;
  iv: number;
}

interface HFDataResponse {
  opt_chart_settings: {
    chart_opt_price_min: number;
    chart_opt_price_max: number;
    chart_iv_min: number;
    chart_iv_max: number;
  };
  global_data: {
    expiration_date: string;
    underlying_ticker: string;
    option_ticker: string;
    strike_price: string;
  };
  option_bid: PricingData[];
  option_ask: PricingData[];
  option_trades: OptionTradesData[];

  option_iv: OptionIvData[];

  underlying_trades: PricingData[];
  underlying_bid: PricingData[];
  underlying_ask: PricingData[];
}

export const OptionTableHF = (props: {
  api: DockviewPanelApi;
  containerApi: DockviewApi;
}) => {
  const [optChartData, setOptChartData] = React.useState<any[]>([]);
  const [optLayout, setOptLayout] = React.useState<any>({});

  const [undChartData, setUndChartData] = React.useState<any[]>([]);
  const [undLayout, setUndLayout] = React.useState<any>({});

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const [globalData, setGlobalData] = React.useState<any>(null);

  const [dataRequest, setDataRequest] = React.useState<any>({
    ticker: "AAPL  251219C00230000",
    startDate: "2025-08-05 09:30",
    endDate: "2025-08-05 09:32",
    interval: "m",
  });

  async function setData() {
    setIsLoading(true);
    setErrorMessage("");
    setGlobalData(null);

    let responseData: HFDataResponse | null = null;

    try {
      const resp = await api.post<HFDataResponse>("/opt-nbbo-hf", dataRequest);
      console.log("Response data: ", resp.data);
      responseData = resp.data;
    } catch (err: any) {
      let errMsg = "";
      if (err?.response?.data?.detail) {
        errMsg = err.response.data.detail;
      } else if (err?.response?.statusText) {
        errMsg = err.response.statusText;
      } else if (err?.message) {
        errMsg = err.message;
      }

      setErrorMessage(errMsg || "Request failed");

      return;
    }

    // OPTION CHART
    const traceOptAsks = {
      type: "scatter",
      x: responseData.option_ask.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.option_ask.map((d) => d.price),
      customdata: responseData.option_ask.map((d) => [
        d.ts_event,
        d.price,
        d.size,
      ]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" +
        "<b>[Ask] Price:</b> %{y}<br>" +
        "<b>Size:</b> %{customdata[2]}<br>" +
        "<extra></extra>", // removes trace name in hover box
      name: "Asks",
      line: {
        color: "green",
        width: 1,
      },
    };

    // trades
    const traceOptTrades = {
      type: "scatter",
      x: responseData.option_trades.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.option_trades.map((d) => d.price),
      customdata: responseData.option_trades.map((d) => [
        d.ts_event,
        d.price,
        d.size,
        d.underlying_price,
        d.iv,
      ]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" +
        "<b>[Trade] Price:</b> %{y}<br>" +
        "<b>Size:</b> %{customdata[2]}<br>" +
        "<b>Underlying Price:</b> %{customdata[3]}<br>" +
        "<b>IV:</b> %{customdata[4]}<br>" +
        "<extra></extra>", // removes trace name in hover box
      name: "Trades",
      line: {
        color: "blue",
        width: 1,
      },

      mode: "lines+markers",
      marker: {
        size: 3, // bigger circle size (default is ~6)
        symbol: "circle", // can be "circle", "square", "diamond", etc
      },
    };

    // bids
    const traceOptBids = {
      type: "scatter",
      x: responseData.option_bid.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.option_bid.map((d) => d.price),
      customdata: responseData.option_bid.map((d) => [
        d.ts_event,
        d.price,
        d.size,
      ]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" +
        "<b>[Bid] Price:</b> %{y}<br>" +
        "<b>Size:</b> %{customdata[2]}<br>" +
        "<extra></extra>", // removes trace name in hover box
      name: "Bids",
      line: {
        color: "red",
        width: 1,
      },
    };

    // iv
    const traceOptIv = {
      type: "scatter",
      x: responseData.option_iv.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.option_iv.map((d) => d.iv),
      customdata: responseData.option_iv.map((d) => [d.ts_event, d.iv]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" + "<b>IV:</b> %{y}<br>" + "<extra></extra>", // removes trace name in hover box
      name: "IV",
      line: {
        color: "purple",
        width: 1,
      },
      yaxis: "y2",

      mode: "lines+markers",
      marker: {
        size: 3, // bigger circle size (default is ~6)
        symbol: "circle", // can be "circle", "square", "diamond", etc
      },
    };

    // underlying
    const traceUndTrades = {
      type: "scatter",
      x: responseData.underlying_trades.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.underlying_trades.map((d) => d.price),
      customdata: responseData.underlying_trades.map((d) => [
        d.ts_event,
        d.price,
        d.size,
      ]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" +
        "<b>[Trade] Price:</b> %{y}<br>" +
        "<b>Size:</b> %{customdata[2]}<br>" +
        "<extra></extra>", // removes trace name in hover box
      name: "Und Trades",
      line: {
        color: "blue",
        width: 1,
      },

      mode: "lines+markers",
      marker: {
        size: 3, // bigger circle size (default is ~6)
        symbol: "circle", // can be "circle", "square", "diamond", etc
      },
    };

    const traceUndAsks = {
      type: "scatter",
      x: responseData.underlying_ask.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.underlying_ask.map((d) => d.price),
      customdata: responseData.underlying_ask.map((d) => [
        d.ts_event,
        d.price,
        d.size,
      ]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" +
        "<b>[Ask] Price:</b> %{y}<br>" +
        "<b>Size:</b> %{customdata[2]}<br>" +
        "<extra></extra>", // removes trace name in hover box
      name: "Asks",
      line: {
        color: "red",
        width: 1,
      },
    };

    const traceUndBids = {
      type: "scatter",
      x: responseData.underlying_bid.map(
        (d) => new Date(d.ts_event.replace(" ", "T"))
      ),
      y: responseData.underlying_bid.map((d) => d.price),
      customdata: responseData.underlying_bid.map((d) => [
        d.ts_event,
        d.price,
        d.size,
      ]),
      hovertemplate:
        "<b>Time:</b> %{x}<br>" +
        "<b>[Bid] Price:</b> %{y}<br>" +
        "<b>Size:</b> %{customdata[2]}<br>" +
        "<extra></extra>", // removes trace name in hover box
      name: "Bids",
      line: {
        color: "green",
        width: 1,
      },
    };

    setGlobalData(responseData.global_data);

    setOptChartData([traceOptBids, traceOptAsks, traceOptTrades, traceOptIv]);
    setUndChartData([traceUndBids, traceUndAsks, traceUndTrades]);

    setOptLayout({
      dragmode: "zoom",
      autosize: true,
      margin: { l: 40, r: 90, t: 0, b: 40 },
      showlegend: true,
      legend: {
        x: 1,
        y: 0,
        xanchor: "right",
        yanchor: "bottom",
      },
      hovermode: "closest", // crosshairs
      hoverdistance: 25,
      xaxis: {
        autorange: true,
        domain: [0, 1],
        rangeslider: { visible: false },
        // range: ['2017-01-03 12:00', '2017-02-15 12:00'],
        type: "date",
        // crosshairs
        showspikes: true,
        spikesnap: "cursor",
        spikecolor: "#999",
        spikethickness: 1,
        spikemode: "toaxis+across+marker",
        spikedash: "solid",
        showgrid: true,
      },
      yaxis: {
        // autorange: true,
        range: [
          responseData.opt_chart_settings.chart_opt_price_min,
          responseData.opt_chart_settings.chart_opt_price_max,
        ],
        domain: [0, 1],
        // range: [114.609999778, 137.410004222],
        type: "linear",
        // crosshairs
        showspikes: true,
        spikemode: "toaxis+across+marker",
        spikesnap: "cursor",
        spikedash: "solid",
        spikecolor: "#999",
        spikethickness: 1,
        showgrid: true,
      },
      yaxis2: {
        range: [
          responseData.opt_chart_settings.chart_iv_min,
          responseData.opt_chart_settings.chart_iv_max,
        ],
        tickfont: { color: "#d62728" },
        anchor: "x",
        overlaying: "y",
        side: "right",
      },
    });

    setUndLayout({
      dragmode: "zoom",
      autosize: true,
      margin: { l: 40, r: 90, t: 0, b: 40 },
      showlegend: true,
      legend: {
        x: 1, // left side
        y: 0, // bottom
        xanchor: "right",
        yanchor: "bottom",
      },

      hovermode: "closest", // crosshairs
      hoverdistance: 25,
      xaxis: {
        autorange: true,
        domain: [0, 1],
        rangeslider: { visible: false },
        // range: ['2017-01-03 12:00', '2017-02-15 12:00'],
        type: "date",
        // crosshairs
        showspikes: true,
        spikesnap: "cursor",
        spikecolor: "#999",
        spikethickness: 1,
        spikemode: "toaxis+across+marker",
        spikedash: "solid",
        showgrid: true,
      },
      yaxis: {
        autorange: true,
        domain: [0, 1],
        // range: [114.609999778, 137.410004222],
        type: "linear",
        // crosshairs
        showspikes: true,
        spikemode: "toaxis+across+marker",
        spikesnap: "cursor",
        spikedash: "solid",
        spikecolor: "#999",
        spikethickness: 1,
        showgrid: true,
      },
    });

    setIsLoading(false);
  }

  return (
    <div className="overflow-y-scroll h-full p-2">
      <form
        className="flex flex-row m-2 gap-2 items-end"
        onSubmit={(e) => {
          e.preventDefault();
          setData();
        }}
      >
        <label htmlFor="ticker">
          Ticker:
          <input
            placeholder="Option ticker"
            type="text"
            id="ticker"
            name="startDate"
            value={dataRequest.ticker}
            onChange={(e) => {
              setDataRequest({
                ...dataRequest,
                ticker: e.target.value,
              });
            }}
          />
        </label>

        <label htmlFor="startDate">
          Start Date:
          <input
            placeholder="YYYY-MM-DD HH:MM"
            type="text"
            id="startDate"
            name="startDate"
            value={dataRequest.startDate}
            onChange={(e) => {
              setDataRequest({
                ...dataRequest,
                startDate: e.target.value,
              });
            }}
          />
        </label>

        <label htmlFor="endDate">
          End Date:
          <input
            placeholder="YYYY-MM-DD HH:MM"
            type="text"
            id="endDate"
            name="endDate"
            value={dataRequest.endDate}
            onChange={(e) => {
              setDataRequest({
                ...dataRequest,
                endDate: e.target.value,
              });
            }}
          />
        </label>

        <button type="submit" className="hidden">
          Submit
        </button>
      </form>

      {errorMessage && (
        <p className="text-red-500 font-weight-bold">Error: {errorMessage}</p>
      )}

      <GlobalData data={globalData} />

      <div>
        <Plotter data={optChartData} layout={optLayout} isLoading={isLoading} />
      </div>

      <div>
        <Plotter data={undChartData} layout={undLayout} isLoading={isLoading} />
      </div>
    </div>
  );
};

const GlobalData = (props: { data: any }) => {
  if (!props.data) {
    return <div></div>;
  }

  return (
    <div className="p-2 border border-gray-300 rounded mb-4">
      <h2 className="text-lg font-bold mb-2">Info</h2>
      <p>{props.data.underlying_ticker}</p>
      <p>{props.data.option_ticker}</p>
      <p>{props.data.strike_price}</p>
      <p>{props.data.expiration_date}</p>
    </div>
  );
};

const Plotter = (props: { data: any[]; layout: any; isLoading: boolean }) => {
  const idRef = React.useRef(Math.random().toString(36).slice(2));
  // deep copy: Plotly writes zoom/pan ranges into the layout's nested objects, so each chart
  // needs its own - never hand it a layout another chart could also be using
  const layout = React.useMemo(
    () => ({ ...structuredClone(props.layout), uirevision: idRef.current }),
    [props.layout]
  );
  if (props.isLoading) {
    return <div className="text-gray-500">Loading...</div>;
  }

  if (props.data.length === 0) {
    return <p className="text-gray-500">No data to display</p>;
  }

  return (
    <Plot
      data={props.data}
      layout={layout}
      useResizeHandler={true}
      style={{
        width: "100%",
      }}
    />
  );
};
